import { notifications } from '@mantine/notifications';
import { playNewOrderSound } from './new-order-sound';

const shownToastIds = new Set<string>();

function formatAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(amount);
}

function showStickyOrderToast(params: {
  id: string;
  title: string;
  message: string;
  color: string;
  className: string;
  onOpen?: () => void;
}) {
  if (shownToastIds.has(params.id)) return;
  shownToastIds.add(params.id);

  playNewOrderSound();

  notifications.show({
    id: params.id,
    title: params.title,
    message: params.message,
    color: params.color,
    autoClose: false,
    withCloseButton: true,
    // classNames.root — не затирает классы контейнера Mantine (иначе пропадает gap)
    classNames: { root: params.className },
    onClick: (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button')) return;
      params.onOpen?.();
      notifications.hide(params.id);
    },
  });
}

export function showNewOrderToast(params: {
  orderId: number;
  orderNumber: string;
  totalAmount?: number;
  onOpen?: () => void;
}) {
  const amount =
    params.totalAmount != null && Number.isFinite(params.totalAmount)
      ? formatAmount(params.totalAmount)
      : '';

  showStickyOrderToast({
    id: `new-order-${params.orderId}`,
    title: 'Новый заказ',
    message: amount
      ? `Заказ ${params.orderNumber} · ${amount}`
      : `Заказ ${params.orderNumber}`,
    color: 'teal',
    className: 'seller-new-order-toast',
    onOpen: params.onOpen,
  });
}

export function showOrderCanceledToast(params: {
  orderId: number;
  orderNumber: string;
  onOpen?: () => void;
}) {
  showStickyOrderToast({
    id: `order-canceled-${params.orderId}`,
    title: 'Заказ отменён клиентом',
    message: `Заказ ${params.orderNumber}`,
    color: 'orange',
    className: 'seller-order-canceled-toast',
    onOpen: params.onOpen,
  });
}

export function showOrderDostavistaToast(params: {
  orderId: number;
  orderNumber: string;
  kind: 'canceled' | 'search_again';
  onOpen?: () => void;
}) {
  const isCanceled = params.kind === 'canceled';
  showStickyOrderToast({
    id: `order-dostavista-${params.kind}-${params.orderId}`,
    title: isCanceled
      ? 'Доставка Достависты отменена'
      : 'Курьер Достависты снялся',
    message: isCanceled
      ? `Заказ ${params.orderNumber} — заявка в Достависте отменена`
      : `Заказ ${params.orderNumber} — снова ищем курьера`,
    color: 'orange',
    className: 'seller-order-dostavista-toast',
    onOpen: params.onOpen,
  });
}

/** Обычный автозакрывающийся тост для inbox-уведомлений. */
export function showSellerInboxToast(params: {
  id?: number;
  title: string;
  body?: string | null;
  onOpen?: () => void;
}) {
  const toastId =
    params.id != null && params.id > 0
      ? `seller-inbox-${params.id}`
      : `seller-inbox-${params.title}-${Date.now()}`;

  if (params.id != null && params.id > 0) {
    if (shownToastIds.has(toastId)) return;
    shownToastIds.add(toastId);
  }

  playNewOrderSound();

  notifications.show({
    id: toastId,
    title: 'Новое уведомление',
    message: params.body?.trim() || params.title,
    color: 'blue',
    autoClose: 5000,
    withCloseButton: true,
    onClick: (event) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('button')) return;
      params.onOpen?.();
      notifications.hide(toastId);
    },
  });
}
